import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useCreateAutoApplyPreviewMutation } from './__generated__/ApplicationTabContent.generated';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { ApplicationTabContentFieldsFragment } from '@/pages/auto-apply/review/__generated__/ApplicationTabContentFragments.generated';
import {
    getCachedPreview,
    setCachedPreview,
    deleteCachedPreview,
} from '../confirmation/previewCache';
import { downloadPreviewHtml } from './downloadPreviewHtml';
import { ROOT_TRACKING_KEY_HEADER } from '../../../lib/apollo/headers';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';
import { getRumClient } from '../../../lib/rum/rumClient';
import { isNetworkError, logRumError } from '../../../lib/rum/rumErrorLogger';

export interface UseApplicationPreviewParams {
    draftId: string | undefined;
    fragment: ApplicationTabContentFieldsFragment;
    skipPreviewGeneration?: boolean;
    /** Embed the AI-autofill disclaimer in the preview HTML. `undefined` = still resolving; preview
     * generation is deferred until it is defined. Required (as `boolean | undefined`) so every caller
     * makes an explicit choice — omitting it would silently defer the preview forever. */
    showAiAutofillDisclaimer: boolean | undefined;
}

export interface UseApplicationPreviewResult {
    html: string | null;
    demographicQuestionsHtml: string | null;
    isLoading: boolean;
    error: undefined | 'permanent' | 'temporary';
}

const decodeBase64 = (encoded: string): string => {
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
};

export function useApplicationPreview({
    draftId,
    fragment,
    skipPreviewGeneration = false,
    showAiAutofillDisclaimer,
}: UseApplicationPreviewParams): UseApplicationPreviewResult {
    const { countryCode, previewWebappBaseUrl } = useInitialState();
    const { getGroup } = useProctorGroups();
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    const showDqAiAutofillDisclaimer =
        isProgressiveAutomation && getGroup('ia_dq_questions_prefill_tst')?.value === 1;
    const location = useLocation();
    const eventLogger = useEventLogger();
    const [html, setHtml] = useState<string | null>(null);
    const [demographicQuestionsHtml, setDemographicQuestionsHtml] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(!skipPreviewGeneration);
    const [error, setError] = useState<undefined | 'permanent' | 'temporary'>(undefined);

    const [
        createPreview,
        { called, data: previewData, loading: mutationLoading, error: mutationError },
    ] = useCreateAutoApplyPreviewMutation();

    const iaUid = fragment.iaUid;
    const jobId = fragment.applyableJob?.jobId;

    const triggerPreviewGeneration = useCallback(() => {
        if (!draftId) return;
        const proctorGroups = fragment.proctors
            .split(',')
            .map((allocation) => allocation.trim())
            .filter(Boolean);
        void createPreview({
            variables: {
                input: {
                    draftManagementTelemetry: {
                        proctorGroups,
                        locale: fragment.locale,
                        countryCode,
                    },
                    showAiAutofillDisclaimer: showAiAutofillDisclaimer ?? true,
                    showDqAiAutofillDisclaimer,
                },
                smartApplyApplicationStateInput: { id: draftId },
            },
            context: {
                headers: {
                    ...(iaUid && { [ROOT_TRACKING_KEY_HEADER]: iaUid }),
                },
            },
        }).catch(() => {});
    }, [
        draftId,
        createPreview,
        fragment.proctors,
        fragment.locale,
        countryCode,
        iaUid,
        showAiAutofillDisclaimer,
        showDqAiAutofillDisclaimer,
    ]);

    // Effect 1: mount trigger — try cache first, fall back to mutation
    useEffect(() => {
        // When skipping preview generation, set loading to false and return
        if (skipPreviewGeneration) {
            setIsLoading(false);
            return;
        }

        // Wait for the SQ prefill decision before generating/cache-checking.
        if (showAiAutofillDisclaimer === undefined) return;

        // If we've already called the mutation, don't call again
        if (!draftId || called) return;

        if (!jobId) {
            eventLogger.logReviewError?.({
                currentPage: location.pathname,
                errorType: { string: 'preview-missing-job-id' },
                errorMessage: { string: 'Missing jobId on fragment, cannot generate preview' },
                draftId: AvroOptional.string(draftId),
                graphqlOperation: AvroOptional.string(null),
            });
            setIsLoading(false);
            setError('temporary');
            return;
        }

        // We're about to attempt loading, ensure loading state is true
        setIsLoading(true);

        const draftState = fragment.state;
        const cachedPreview = getCachedPreview(draftId);
        const latestPreviewId = fragment.lastPreviewId;

        if (
            draftState === 'PREVIEW' &&
            latestPreviewId &&
            cachedPreview?.previewId === latestPreviewId &&
            // DQ may be missing after reload or in legacy entries; the downloaded file excludes it.
            cachedPreview.demographicQuestionAnswersHTML !== undefined &&
            cachedPreview.previewHtmlFileToken
        ) {
            let cancelled = false;

            void downloadPreviewHtml(
                cachedPreview.previewHtmlFileToken,
                iaUid ?? '',
                previewWebappBaseUrl,
            )
                .then((downloaded) => {
                    if (cancelled) return;
                    if (downloaded) {
                        let decodedDqHtml: string | null;
                        try {
                            decodedDqHtml = cachedPreview.demographicQuestionAnswersHTML
                                ? decodeBase64(cachedPreview.demographicQuestionAnswersHTML)
                                : null;
                        } catch {
                            eventLogger.logReviewError?.({
                                currentPage: location.pathname,
                                errorType: { string: 'preview-cached-dq-decode-error' },
                                errorMessage: { string: 'Cannot decode cached DQ preview' },
                                draftId: AvroOptional.string(draftId),
                                graphqlOperation: AvroOptional.string(null),
                            });
                            deleteCachedPreview(draftId);
                            triggerPreviewGeneration();
                            return;
                        }
                        setDemographicQuestionsHtml(decodedDqHtml);
                        setHtml(downloaded);
                        setIsLoading(false);
                    } else {
                        deleteCachedPreview(draftId);
                        triggerPreviewGeneration();
                    }
                })
                .catch((err: unknown) => {
                    if (cancelled) return;
                    eventLogger.logReviewError?.({
                        currentPage: location.pathname,
                        errorType: { string: 'preview-download-error' },
                        errorMessage: AvroOptional.string(
                            err instanceof Error ? err.message : String(err),
                        ),
                        draftId: AvroOptional.string(draftId),
                        graphqlOperation: { string: 'downloadPreviewHtml' },
                    });
                    deleteCachedPreview(draftId);
                    triggerPreviewGeneration();
                });
            return () => {
                cancelled = true;
            };
        }

        triggerPreviewGeneration();
    }, [
        skipPreviewGeneration,
        showAiAutofillDisclaimer,
        draftId,
        called,
        jobId,
        fragment,
        iaUid,
        previewWebappBaseUrl,
        triggerPreviewGeneration,
        eventLogger,
        location.pathname,
    ]);

    // Effect 2: mutation completion — decode HTML or trigger failure
    useEffect(() => {
        if (!called || mutationLoading || !draftId) return;

        // Check for errorCode in preview response (permanent error)
        // Apollo error link does not fire here — the mutation succeeded at the protocol level;
        // errorCode is an application-level error embedded in the response payload.
        const errorCode = previewData?.smartApplyApplicationState.createPreview?.errorCode;
        if (errorCode) {
            eventLogger.logReviewError?.({
                currentPage: location.pathname,
                errorType: { string: 'preview-creation-error' },
                errorMessage: { string: errorCode },
                draftId: AvroOptional.string(draftId),
                graphqlOperation: { string: 'createAutoApplyPreview' },
            });
            setIsLoading(false);
            setError('permanent');
            return;
        }

        if (mutationError) {
            setIsLoading(false);
            // Permanent errors: GraphQL errors where retry won't help
            // Temporary errors: Network/timeout errors where retry might help
            const isPermanent = mutationError.graphQLErrors.length > 0;
            setError(isPermanent ? 'permanent' : 'temporary');
            return;
        }

        const rawHtml = previewData?.smartApplyApplicationState.createPreview?.previewHtml;
        if (!rawHtml) {
            eventLogger.logReviewError?.({
                currentPage: location.pathname,
                errorType: { string: 'preview-empty-response' },
                errorMessage: {
                    string: 'Mutation succeeded but returned no errorCode and no previewHtml',
                },
                draftId: AvroOptional.string(draftId),
                graphqlOperation: { string: 'createAutoApplyPreview' },
            });
            setIsLoading(false);
            setError('temporary');
            return;
        }

        try {
            const preview = previewData.smartApplyApplicationState.createPreview;
            const decodedHtml = decodeBase64(rawHtml);
            const decodedDqHtml = preview?.demographicQuestionAnswersHTML
                ? decodeBase64(preview.demographicQuestionAnswersHTML)
                : null;
            if (preview) {
                setCachedPreview(draftId, {
                    previewId: preview.previewId ?? '',
                    previewHtmlFileToken: preview.previewHtmlFileToken,
                    encryptedApplicationId: preview.encryptedApplicationId ?? '',
                    previewHtmlContentHash: preview.previewHtmlContentHash,
                    demographicQuestionAnswersHTML: preview.demographicQuestionAnswersHTML ?? null,
                });
            }
            setHtml(decodedHtml);
            setDemographicQuestionsHtml(decodedDqHtml);
            setIsLoading(false);
        } catch (err) {
            eventLogger.logReviewError?.({
                currentPage: location.pathname,
                errorType: { string: 'preview-decode-error' },
                errorMessage: AvroOptional.string(err instanceof Error ? err.message : String(err)),
                draftId: AvroOptional.string(draftId),
                graphqlOperation: { string: 'createAutoApplyPreview' },
            });
            setIsLoading(false);
            setError('temporary');
        }
    }, [
        called,
        mutationLoading,
        mutationError,
        previewData,
        draftId,
        eventLogger,
        location.pathname,
    ]);

    // Only the final user-visible failure affects availability. Cache/download failures
    // that regenerate successfully remain Signals diagnostics, not failed views.
    const reportedFailure = useRef(false);
    useEffect(() => {
        if (!error) {
            reportedFailure.current = false;
            return;
        }
        if (reportedFailure.current) return;
        reportedFailure.current = true;
        logRumError(getRumClient(), mutationError, {
            customErrorType: 'AutoApplyPreviewError',
            sanitizedMessage: 'AutoApplyPreviewError',
            isPageFailure: !isNetworkError(mutationError),
        });
    }, [error, mutationError]);

    return { html, demographicQuestionsHtml, isLoading, error };
}
